import { HTMLAttributes } from "react";
import { cn } from "@/utils/cn";
import Image from "@/components/common/Image";
import { Button } from "@/components/common/Button/Button";

interface RecognitionBadge {
  src: string;
  alt: string;
  label?: string; // Added for mobile labels
}

interface RecognitionProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  description: string;
  badges: RecognitionBadge[];
  tag?: string;
  learnMoreHref?: string;
  tagIcon?: React.ReactNode;
}

export const Recognition = ({
  title,
  description,
  badges,
  tag,
  tagIcon,
  learnMoreHref,
  className,
  ...props
}: RecognitionProps) => {
  return (
    <section className={cn("py-12 md:py-20 my-5", className)} {...props}>
      <div className="container mx-auto px-4">
        {/* Mobile-only view */}
        <div className="flex lg:hidden flex-col items-center text-center">
          {tag && (
            <div className="inline-flex items-center px-6 py-2 bg-primary-100 text-primary-700 rounded-full text-base font-medium mb-4">
              {tagIcon && <span className="mr-2">{tagIcon}</span>}
              {tag}
            </div>
          )}

          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            {title}
          </h2>

          <p className="text-base text-gray-600 mb-6 max-w-lg mx-auto">
            {description}
          </p>

          {learnMoreHref && (
            <div className="mb-8 w-full px-4">
              <a href={learnMoreHref} target="_blank" rel="noopener noreferrer" className="block w-full">
                <Button
                  variant="gradient"
                  className="w-full px-8 py-3 border border-gray-300 rounded-lg"
                >
                  Learn more
                </Button>
              </a>
            </div>
          )}

          <div className="grid grid-cols-2 gap-x-6 gap-y-10 w-full mt-4">
            {badges.map((badge, index) => (
              <div key={`mobile-${index}`} className="flex flex-col items-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24">
                  <Image
                    src={badge.src}
                    alt={badge.alt}
                    className="w-full h-full object-contain"
                  />
                </div>
                {badge.label && (
                  <span className="text-xs text-gray-600 mt-2">{badge.label}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Desktop-only view - original code unchanged */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="max-w-xl mx-auto lg:mx-0 text-center lg:text-left">
            {tag && (
              <span className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4 md:mb-6">
                {tag}
              </span>
            )}
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              {title}
            </h2>
            <p className="text-lg md:text-xl text-gray-600 mb-6">{description}</p>
            {learnMoreHref && (
              <div className="flex justify-center lg:justify-start mb-8 lg:mb-0">
                <a href={learnMoreHref} target="_blank" rel="noopener noreferrer">
                  <Button variant="gradient" color="primary">
                    Learn more
                  </Button>
                </a>
              </div>
            )}
          </div>
          <div className="flex justify-center lg:justify-end mt-8 lg:mt-0">
            <div className="flex flex-row flex-nowrap items-center gap-2 sm:gap-4 md:gap-6 overflow-visible w-full justify-center">
              {badges.map((badge, index) => (
                <div
                  key={`desktop-${index}`}
                  className="relative w-[70px] sm:w-[90px] md:w-[120px] h-auto"
                >
                  <Image
                    src={badge.src}
                    alt={badge.alt}
                    className="w-full h-auto"
                    style={{
                      objectPosition: "center center",
                      maxHeight: "155px",
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};